import { useEffect, useState } from "react";
import { api, API_BASE, formatApiError } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Download, Search, Trash2, AlertTriangle, MailCheck, Database } from "lucide-react";
import { toast } from "sonner";

export default function Files() {
  const [files, setFiles] = useState([]);
  const [q, setQ] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [loading, setLoading] = useState(false);
  const [renameOpen, setRenameOpen] = useState(false);
  const [target, setTarget] = useState(null);
  const [customName, setCustomName] = useState("");
  const [includeDuplicates, setIncludeDuplicates] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkMode, setBulkMode] = useState("filter"); // selected | filter | all

  // Push-to-CRM dialog
  const [pushOpen, setPushOpen] = useState(false);
  const [pushTarget, setPushTarget] = useState(null);
  const [pushName, setPushName] = useState("");
  const [pushing, setPushing] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (start) params.start_date = start;
      if (end) params.end_date = end;
      if (q) params.q = q;
      const { data } = await api.get("/files", { params });
      setFiles(data.files || []);
      setSelected(new Set()); // reset selection on refresh
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const openRenameForFile = (f) => {
    setTarget({ file_id: f.id });
    setCustomName(f.original_name.replace(/\.[^.]+$/, ""));
    setIncludeDuplicates(true);
    setRenameOpen(true);
  };
  const openRenameForRange = () => {
    if (!start && !end) return toast.error("Select a date first");
    setTarget({ start_date: start || undefined, end_date: end || undefined });
    setCustomName(`omnilink-leads_${start || "start"}_to_${end || "now"}`);
    setIncludeDuplicates(true);
    setRenameOpen(true);
  };

  const doDownload = async () => {
    if (!customName.trim()) return toast.error("Filename cannot be empty");
    const params = new URLSearchParams();
    if (target?.file_id) params.set("file_id", target.file_id);
    if (target?.start_date) params.set("start_date", target.start_date);
    if (target?.end_date) params.set("end_date", target.end_date);
    if (!includeDuplicates) params.set("exclude_duplicates", "true");
    params.set("custom_name", customName);
    const token = localStorage.getItem("omnilink_token") || "";
    try {
      const res = await fetch(`${API_BASE}/download?${params.toString()}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error(`Download failed (${res.status})`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = customName.endsWith(".csv") ? customName : `${customName}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Download started");
      setRenameOpen(false);
    } catch (e) {
      toast.error(e.message || "Download failed");
    }
  };

  const onDelete = async (f) => {
    if (!window.confirm(`Delete "${f.original_name}" and ${f.total_rows} leads?`)) return;
    try {
      await api.delete(`/files/${f.id}`);
      toast.success("File deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const onPushToCRM = (f) => {
    setPushTarget(f);
    setPushName(f.original_name.replace(/\.[^.]+$/, ""));
    setPushOpen(true);
  };

  const doPushToCRM = async () => {
    if (!pushTarget) return;
    if (!pushName.trim()) return toast.error("Filename cannot be empty");
    setPushing(true);
    try {
      const { data } = await api.post("/crm/push", {
        file_ids: [pushTarget.id],
        rename_map: { [pushTarget.id]: pushName.trim() },
      });
      const r = (data.results || [])[0] || {};
      if (r.status === "ok") {
        toast.success(`Sent ${r.pushed_filename} (${r.rows.toLocaleString()} rows) to CRM`);
        setPushOpen(false);
      } else {
        toast.error(`Push failed: ${(r.errors || ["unknown"]).join("; ")}`);
      }
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setPushing(false);
    }
  };

  const toggleRow = (id) => {
    const n = new Set(selected);
    if (n.has(id)) n.delete(id); else n.add(id);
    setSelected(n);
  };
  const allSelected = files.length > 0 && files.every((f) => selected.has(f.id));
  const toggleAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(files.map((f) => f.id)));
  };

  const runBulkDelete = async () => {
    try {
      let body = {};
      if (bulkMode === "selected") {
        if (selected.size === 0) return toast.error("No rows selected");
        body = { file_ids: Array.from(selected) };
      } else if (bulkMode === "filter") {
        body = {};
        if (start) body.start_date = start;
        if (end) body.end_date = end;
        if (q) body.q = q;
        if (!body.start_date && !body.end_date && !body.q) return toast.error("Apply a filter first");
      } else if (bulkMode === "all") {
        body = { all: true };
      }
      const { data } = await api.post("/files/bulk-delete", body);
      toast.success(`Deleted ${data.deleted_files} files · ${data.deleted_leads.toLocaleString()} leads`);
      setBulkOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div className="p-6 md:p-10 max-w-[1500px]" data-testid="files-page">
      <div className="mb-8 fade-up">
        <div className="text-[11px] font-semibold tracking-[0.22em] uppercase text-gray-500">Archive</div>
        <h1 className="font-display text-4xl font-bold tracking-tight text-gray-950 mt-2">Files</h1>
        <p className="text-gray-600 mt-2">Browse uploaded files. Download or bulk-delete by date / name / all.</p>
      </div>

      <Card className="rounded-sm border border-gray-200 shadow-none p-5 bg-white mb-5">
        <div className="grid md:grid-cols-6 gap-3">
          <div className="md:col-span-2">
            <Label className="text-[10px] tracking-[0.18em] uppercase text-gray-600">Search filename</Label>
            <div className="relative mt-1">
              <Search size={14} className="absolute left-3 top-3 text-gray-400" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="e.g. 2025-09" className="pl-9 h-10 rounded-sm" data-testid="files-search-input" />
            </div>
          </div>
          <div>
            <Label className="text-[10px] tracking-[0.18em] uppercase text-gray-600">From</Label>
            <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="mt-1 h-10 rounded-sm" data-testid="files-start-date" />
          </div>
          <div>
            <Label className="text-[10px] tracking-[0.18em] uppercase text-gray-600">To</Label>
            <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="mt-1 h-10 rounded-sm" data-testid="files-end-date" />
          </div>
          <div className="flex items-end gap-2">
            <Button onClick={load} className="flex-1 rounded-sm h-10 bg-[#002FA7] hover:bg-[#002480] text-white" data-testid="files-apply-filter">Apply</Button>
            <Button variant="outline" className="rounded-sm h-10" onClick={openRenameForRange} data-testid="download-range-button">
              <Download size={14} className="mr-1" /> Range
            </Button>
          </div>
          <div className="flex items-end">
            <Button
              variant="outline"
              className="w-full rounded-sm h-10 text-[#FF3B30] border-[#FF3B30]/40 hover:bg-[#FF3B30]/5"
              onClick={() => { setBulkMode(selected.size > 0 ? "selected" : "filter"); setBulkOpen(true); }}
              data-testid="bulk-delete-button"
            >
              <Trash2 size={14} className="mr-1" /> Bulk Delete
            </Button>
          </div>
        </div>
        {selected.size > 0 && (
          <div className="mt-3 text-sm text-gray-700 flex items-center gap-3" data-testid="selection-info">
            <span className="font-semibold">{selected.size}</span> file{selected.size === 1 ? "" : "s"} selected
            <button onClick={() => setSelected(new Set())} className="text-[#002FA7] underline text-xs">Clear</button>
          </div>
        )}
      </Card>

      <Card className="rounded-sm border border-gray-200 shadow-none bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="files-table">
            <thead className="bg-gray-50">
              <tr className="text-[10px] tracking-wider uppercase text-gray-500">
                <th className="px-3 py-3 w-10">
                  <Checkbox checked={allSelected} onCheckedChange={toggleAll} data-testid="files-select-all" />
                </th>
                <th className="text-left px-4 py-3">File</th>
                <th className="text-left px-4 py-3">Source Date</th>
                <th className="text-right px-4 py-3">Rows</th>
                <th className="text-right px-4 py-3">New</th>
                <th className="text-right px-4 py-3">Dup (5/30/60/90/All)</th>
                <th className="text-left px-4 py-3">Uploaded</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (<tr><td colSpan={8} className="px-4 py-8 text-center text-gray-500">Loading…</td></tr>)}
              {!loading && files.length === 0 && (
                <tr><td colSpan={8} className="px-4 py-12 text-center text-gray-500">No files. Upload your first one to get started.</td></tr>
              )}
              {!loading && files.map((f) => (
                <tr key={f.id} className={`border-t border-gray-100 hover:bg-gray-50 ${selected.has(f.id) ? "bg-[#002FA7]/5" : ""}`} data-testid={`file-row-${f.id}`}>
                  <td className="px-3 py-3"><Checkbox checked={selected.has(f.id)} onCheckedChange={() => toggleRow(f.id)} data-testid={`file-check-${f.id}`} /></td>
                  <td className="px-4 py-3 max-w-[320px]" title={f.original_name}>
                    <div className="truncate">{f.original_name}</div>
                    {f.last_sent_at && (
                      <span
                        className="inline-flex items-center gap-1 mt-1 mr-1 px-1.5 py-0.5 text-[9px] tracking-wider uppercase font-semibold text-[#00C04B] border border-[#00C04B]/30 bg-[#00C04B]/5 rounded-sm"
                        title={`Sent to: ${f.last_sent_to || "—"}`}
                        data-testid={`sent-badge-${f.id}`}
                      >
                        <MailCheck size={9} /> Sent {(f.last_sent_at || "").slice(0, 10)}
                      </span>
                    )}
                    {f.last_pushed_at && (
                      <span
                        className="inline-flex items-center gap-1 mt-1 px-1.5 py-0.5 text-[9px] tracking-wider uppercase font-semibold text-[#007AFF] border border-[#007AFF]/30 bg-[#007AFF]/5 rounded-sm"
                        title={`Last push status: ${f.last_push_status || "—"}; pushed: ${f.pushed_count || 0}`}
                        data-testid={`pushed-badge-${f.id}`}
                      >
                        <Database size={9} /> CRM {(f.last_pushed_at || "").slice(0, 10)}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 font-mono">{f.source_date}</td>
                  <td className="px-4 py-3 text-right font-mono">{f.total_rows}</td>
                  <td className="px-4 py-3 text-right font-mono text-[#00C04B]">{f.new_leads}</td>
                  <td className="px-4 py-3 text-right font-mono text-xs">
                    {f.dup_5 ?? 0}/{f.dup_30}/{f.dup_60}/{f.dup_90}/<span className="text-[#FF3B30]">{f.dup_all}</span>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-gray-500">{(f.created_at || "").slice(0, 10)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2">
                      <Button size="sm" variant="outline" className="rounded-sm h-8" onClick={() => openRenameForFile(f)} data-testid={`download-file-${f.id}`}>
                        <Download size={14} className="mr-1" /> Download
                      </Button>
                      <Button size="sm" variant="outline" className="rounded-sm h-8 text-[#007AFF] border-[#007AFF]/30 hover:bg-[#007AFF]/5" onClick={() => onPushToCRM(f)} data-testid={`push-crm-${f.id}`} title="Push to CRM">
                        <Database size={14} />
                      </Button>
                      <Button size="sm" variant="outline" className="rounded-sm h-8 text-[#FF3B30] border-[#FF3B30]/30 hover:bg-[#FF3B30]/5" onClick={() => onDelete(f)} data-testid={`delete-file-${f.id}`}>
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Rename / Download dialog */}
      <Dialog open={renameOpen} onOpenChange={setRenameOpen}>
        <DialogContent className="rounded-sm border-gray-200" data-testid="rename-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Rename before download</DialogTitle>
            <DialogDescription className="text-xs text-gray-500">Set the filename you want — .csv is auto-appended.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-[10px] tracking-[0.18em] uppercase text-gray-600">New filename</Label>
              <Input value={customName} onChange={(e) => setCustomName(e.target.value)} placeholder="my-export" className="mt-1 h-10 rounded-sm font-mono" data-testid="rename-input" />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={includeDuplicates} onChange={(e) => setIncludeDuplicates(e.target.checked)} data-testid="rename-include-dups" />
              Include duplicate rows
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setRenameOpen(false)}>Cancel</Button>
            <Button onClick={doDownload} className="rounded-sm bg-[#002FA7] hover:bg-[#002480] text-white" data-testid="rename-confirm-button">
              <Download size={14} className="mr-1" /> Download CSV
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk delete dialog */}
      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent className="rounded-sm border-gray-200" data-testid="bulk-delete-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2"><AlertTriangle className="text-[#FF3B30]" size={18} /> Bulk Delete Files</DialogTitle>
            <DialogDescription className="text-xs text-gray-500">This action cannot be undone. Choose what to remove.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {[
              ["selected", `Delete selected (${selected.size})`, `Removes the ${selected.size} file(s) you checked.`, selected.size === 0],
              ["filter", "Delete by current filter", `Removes every file matching the current search / date filter (${files.length} on screen).`, false],
              ["all", "Delete ALL files", "Wipes every file and every lead from the database. Use with caution.", false],
            ].map(([val, title, desc, disabled]) => (
              <label
                key={val}
                aria-disabled={disabled ? "true" : "false"}
                className={`block border rounded-sm p-3 transition-all ${
                  disabled
                    ? "border-gray-100 bg-gray-50 opacity-50 cursor-not-allowed"
                    : `cursor-pointer ${bulkMode === val ? "border-[#002FA7] bg-[#002FA7]/5" : "border-gray-200 hover:border-gray-300"}`
                }`}
                data-testid={`bulk-mode-${val}`}
              >
                <input
                  type="radio"
                  name="bulk-mode"
                  value={val}
                  checked={bulkMode === val}
                  disabled={disabled}
                  onChange={() => !disabled && setBulkMode(val)}
                  className="mr-2"
                />
                <span className={`font-medium ${val === "all" ? "text-[#FF3B30]" : "text-gray-900"}`}>{title}</span>
                <div className="text-xs text-gray-500 mt-1 ml-5">{desc}</div>
              </label>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setBulkOpen(false)}>Cancel</Button>
            <Button onClick={runBulkDelete} className="rounded-sm bg-[#FF3B30] hover:bg-[#cc2a22] text-white" data-testid="bulk-delete-confirm">
              <Trash2 size={14} className="mr-1" /> Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Push-to-CRM dialog */}
      <Dialog open={pushOpen} onOpenChange={setPushOpen}>
        <DialogContent className="rounded-sm border-gray-200" data-testid="push-crm-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2"><Database className="text-[#007AFF]" size={18} /> Send file to CRM</DialogTitle>
            <DialogDescription className="text-xs text-gray-500">
              The CRM will receive a CSV file containing 8 columns: phone, email, first_name, last_name, street, city, state, zip.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="text-xs text-gray-600">
              Source file: <span className="font-mono text-gray-900">{pushTarget?.original_name}</span> · <span className="font-mono">{(pushTarget?.total_rows || 0).toLocaleString()} rows</span>
            </div>
            <div>
              <Label className="text-[10px] tracking-[0.18em] uppercase text-gray-600">Filename in CRM</Label>
              <Input
                value={pushName}
                onChange={(e) => setPushName(e.target.value)}
                placeholder="leads-2026-05-30"
                className="mt-1 h-10 rounded-sm font-mono"
                data-testid="push-rename-input"
              />
              <div className="text-xs text-gray-500 mt-1">.csv will be appended automatically.</div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setPushOpen(false)} disabled={pushing}>Cancel</Button>
            <Button
              onClick={doPushToCRM}
              disabled={pushing}
              className="rounded-sm bg-[#007AFF] hover:bg-[#0066D6] text-white"
              data-testid="push-confirm-btn"
            >
              {pushing ? "Sending…" : (<><Database size={14} className="mr-1.5" /> Send to CRM</>)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
